/*  FLOWERTRAIL — house chapters · PSP XMB wave silhouettes, v2 (2026-10-18)
    Look (approved by Aryan, do not change): dark mode, four thin translucent wave silhouettes in
    the house colour — crisp crest hairline + soft halo + slope-lit body fading below — running in
    opposite directions, ADDED so they pass through each other. Construction ported from the ops
    app's psp-wave-gl.ts (PSP XrossMediaBar).
    v2:
      · DESKTOP (≥990px): the waves flow edge-to-edge across the whole chapter — full strength
        around the house banner, dimmer behind the name/text.
      · MOBILE: unchanged look (waves under the lockup, fading at the card's sides).
    Speed (mobile first):
      · ONE thin band canvas per chapter, only as tall as the waves (~0.72 × card height) and, on
        mobile, only as wide as the card → several times fewer pixels than v1's 1.9×-card square
      · WebGL context created lazily when the chapter is within one screen; render loop only while
        the band is on screen; 30fps on touch / 60 on desktop; paused on tab-hide and pinch-zoom
      · (2026-10-18) rendered in a Worker via OffscreenCanvas where supported — same shader and
        timing, zero main-thread frames; the original main-thread loop remains the fallback
      · adaptive resolution: sustained long frames (budget phones) step the backing store down
        (2× → 1.5× → 1× → 0.75× DPR) until the frame budget holds
      · geometry/uniforms recomputed only on resize (ResizeObserver), never per frame
    Logo emergence classes (ft-live--on / ft-live--revealed) stay on the card. */
(function () {
  'use strict';
  if (window.__ftPspWave) return;
  window.__ftPspWave = true;
  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia && matchMedia('(pointer: coarse)').matches;

  var PAL = {
    blissence: { body: '#b8913c', crest: '#f0d998' },   /* champagne gold */
    fragrojet: { body: '#3f74d6', crest: '#b5cdf7' },   /* ice blue */
    roman:     { body: '#c23f2a', crest: '#f0a090' },   /* crimson ember */
    gale:      { body: '#2fa39c', crest: '#a6e6e1' }    /* gale teal */
  };
  /* the band spans card-space y (y up, 0 = card bottom, 1 = card top) from Y0 to Y1 */
  var Y0 = -0.10, Y1 = 0.62;

  var VS = 'attribute vec2 aPos;varying vec2 vUv;void main(){vUv=aPos*.5+.5;gl_Position=vec4(aPos,0.,1.);}';
  var FS = [
    'precision highp float;',
    'varying vec2 vUv;',
    'uniform vec4 uMap;',          /* card space: x = vUv.x*uMap.x + uMap.y ; y = vUv.y*uMap.z + uMap.w */
    'uniform float uPx,uWide;',    /* uPx: one device pixel in card-space y · uWide: 1 desktop flow-across, 0 mobile */
    'uniform float uTime,uReveal;uniform vec3 uBody,uCrest;',
    'void main(){',
    ' vec2 uv=vec2(vUv.x*uMap.x+uMap.y,vUv.y*uMap.z+uMap.w);float t=uTime;float px=uPx;',
    ' vec3 col=vec3(0.);',
    ' for(int i=0;i<4;i++){float fi=float(i);float dir=(i==1||i==3)?-1.:1.;',
    '  float ph=fi*2.1;float sp=.17+.04*fi;float f1=2.2+.3*fi;float f2=4.9+.45*fi;',
    '  float base=.25+.035*fi;',
    '  float a1=uv.x*f1+dir*t*sp+ph;float a2=uv.x*f2-dir*t*sp*1.3+ph*1.6;',
    '  float tx=uv.x-.5;float q=1.+abs(tx)*.6;float tilt=.07*dir*tx/q;',            /* bounded tilt: far waves stay in the band */
    '  float y=base+.06*sin(a1)+.022*sin(a2)+tilt;',
    '  float sl=.06*f1*cos(a1)+.022*f2*cos(a2)+.07*dir/(q*q);',
    '  float d=(uv.y-y)*inversesqrt(1.+sl*sl);',
    '  float lit=.65+.45*clamp(sl*2.2*dir,-1.,1.);',
    '  float ad=abs(d);',
    '  float body=smoothstep(0.,-1.5*px,d)*exp(min(d,0.)*11.1);',
    '  float crest=exp(-ad/(1.2*px))*.9+exp(-ad*66.7)*.12;',
    '  float k=.6+.4*sin(uv.x*1.6+fi*1.3+t*.1*dir);',
    '  col+=(uBody*(lit*body*.30)+uCrest*(crest*.70))*k;}',
    ' float tx=uv.x-.5;',
    ' float wDesk=(.26+.74*exp(-tx*tx*1.6))*smoothstep(0.,.06,vUv.x)*smoothstep(1.,.94,vUv.x);',   /* desktop: dimmer away from the banner */
    ' float wMob=1.-smoothstep(.34,.64,abs(tx));',                                                  /* mobile: the approved card-local fade */
    ' float fy=1.-smoothstep(.16,.34,abs(uv.y-.27));',
    ' col*=mix(wMob,wDesk,uWide)*fy*uReveal;',
    ' float a=min(max(col.r,max(col.g,col.b)),1.);',
    ' gl_FragColor=vec4(col,a);}'
  ].join('\n');

  function hex(h) { var n = parseInt(h.slice(1), 16); return [(n >> 16) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]; }
  var frozen = false;
  window.addEventListener('ft:zoom-start', function () { frozen = true; wPost({ frozen: true }); });
  window.addEventListener('ft:zoom-end', function () { frozen = false; wPost({ frozen: false }); });

  var SCALES = coarse ? [2, 1.5, 1, 0.75] : [1.5, 1.25, 1, 0.75];
  var FRAME_MS = coarse ? 1000 / 30 : 1000 / 60;
  var LOGO_MS = 320;   /* when the 1100ms smoothstep reveal passes 0.2 = the logo-emergence point */

  /* ---------------- perf 2026-10-18: render in a Worker (OffscreenCanvas) ----------------
     The old main-thread loop asked for a frame on EVERY vsync (60/90/120Hz) to draw 30fps, so the
     whole page lifecycle (style, IntersectionObserver, layerize, commit of ~200 layers) ran 2-4x
     more often than needed — ~1.2s of main-thread work per 3s at 4x CPU while a house was on screen.
     The worker runs the SAME shader, dt gate, adaptive resolution and reveal ramp on its own
     thread → identical pixels, zero main-thread frames. Browsers without WebGL-in-OffscreenCanvas
     (iOS < 17, old Firefox) are detected BEFORE any canvas is transferred (a transferred canvas
     can't be taken back) and keep the original main-thread renderer below, unchanged. */
  function waveWorker() {                     /* self-contained: runs inside the worker, no closure vars */
    var K = null, C = {}, frozen = false, hidden = false;
    var raf = self.requestAnimationFrame ? function (f) { self.requestAnimationFrame(f); } : function (f) { setTimeout(function () { f(performance.now()); }, 16); };
    function hex(h) { var n = parseInt(h.slice(1), 16); return [(n >> 16) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]; }
    function initGL(c) {
      var gl = c.cv.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, powerPreference: 'low-power', preserveDrawingBuffer: false });
      if (!gl) return false;
      function sh(type, src) { var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; }
      var prog = gl.createProgram();
      gl.attachShader(prog, sh(gl.VERTEX_SHADER, K.VS)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, K.FS)); gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link');
      gl.useProgram(prog);
      var buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      var loc = gl.getAttribLocation(prog, 'aPos'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      var U = {}; ['uMap', 'uPx', 'uWide', 'uTime', 'uReveal', 'uBody', 'uCrest'].forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });
      gl.uniform3fv(U.uBody, hex(c.P.body)); gl.uniform3fv(U.uCrest, hex(c.P.crest));
      gl.clearColor(0, 0, 0, 0);
      gl.disable(gl.DITHER);
      c.cv.addEventListener('webglcontextlost', function (e) { e.preventDefault(); c.gl = null; c.running = false; }, false);
      c.gl = gl; c.U = U;
      return true;
    }
    function applyScale(c) {
      var gl = c.gl, g = c.geo; if (!gl || !g) return;
      var s = Math.min(c.SCALES[c.scaleIdx], c.dpr || 1);
      var W = Math.max(64, Math.round(g.w * s)), H = Math.max(16, Math.round(g.hgt * s));
      if (c.cv.width !== W || c.cv.height !== H) { c.cv.width = W; c.cv.height = H; }
      gl.viewport(0, 0, W, H);
      gl.uniform4f(c.U.uMap, g.w / g.cardW, (g.x0 - g.cardL) / g.cardW, K.Y1 - K.Y0, K.Y0);
      gl.uniform1f(c.U.uPx, (K.Y1 - K.Y0) / H);
      gl.uniform1f(c.U.uWide, g.wide ? 1 : 0);
    }
    function frame(c, now) {
      if (!c.visible || hidden || !c.gl) { c.running = false; return; }
      var dt = now - c.last;
      if (!frozen && c.geo && dt >= c.frameMs - 1) {
        if (c.last && c.revealed) {                 /* adaptive resolution for budget phones */
          c.sampled++; if (dt > c.frameMs * 1.6) c.slow++;
          if (c.sampled >= 45) { if (c.slow > 18 && c.scaleIdx < c.SCALES.length - 1) { c.scaleIdx++; applyScale(c); } c.sampled = 0; c.slow = 0; }
        }
        c.last = now;
        var rv = c.revealAt ? Math.min(1, (now - c.revealAt) / 1100) : 0; rv = rv * rv * (3 - 2 * rv);
        if (rv > 0.2) c.revealed = true;
        var gl = c.gl;
        gl.uniform1f(c.U.uTime, (now - c.t0) / 1000); gl.uniform1f(c.U.uReveal, rv);
        gl.clear(gl.COLOR_BUFFER_BIT); gl.drawArrays(gl.TRIANGLES, 0, 3);
      }
      raf(function (t) { frame(c, t); });
    }
    function wake(c) { if (!c.running && c.visible && c.gl && !hidden) { c.running = true; c.last = 0; raf(function (t) { frame(c, t); }); } }
    self.addEventListener('message', function (e) {
      var m = e.data, c;
      if (m.init) {                              /* capability probe before any real canvas is transferred */
        K = m.init; var ok = false;
        try { var g = new OffscreenCanvas(1, 1).getContext('webgl'); ok = !!g; var x = g && g.getExtension('WEBGL_lose_context'); if (x) x.loseContext(); } catch (err) { ok = false; }
        self.postMessage({ probe: ok }); return;
      }
      if ('frozen' in m) frozen = m.frozen;
      if ('hidden' in m) { hidden = m.hidden; for (var k in C) wake(C[k]); }
      if (m.id == null) return;
      if (m.canvas) {
        c = C[m.id] = { cv: m.canvas, P: m.P, frameMs: m.frameMs, SCALES: m.SCALES, scaleIdx: 0, gl: null, U: null, geo: null, dpr: 1,
                        visible: false, running: false, revealAt: 0, revealed: false, last: 0, slow: 0, sampled: 0,
                        t0: performance.now() - Math.random() * 40000 };
        var ok2 = false; try { ok2 = initGL(c); } catch (err) { ok2 = false; }
        if (!ok2) { delete C[m.id]; self.postMessage({ id: m.id, fail: true }); return; }
      }
      c = C[m.id]; if (!c) return;
      if (m.geo) { c.geo = m.geo; c.dpr = m.dpr; applyScale(c); }
      if (m.revealAt) c.revealAt = m.revealAt - performance.timeOrigin;   /* absolute epoch ms → worker clock */
      if ('visible' in m) c.visible = m.visible;
      wake(c);
    });
  }

  var wk = null, wState = 0, wWait = [], wCards = {}, wId = 0;   /* wState: 0 unknown/probing · 1 ok · -1 use main thread */
  function wPost(m) { if (wk && wState === 1) wk.postMessage(m); }
  function wSettle(ok) {
    if (wState) return;
    wState = ok ? 1 : -1;
    if (!ok && wk) { try { wk.terminate(); } catch (e) {} wk = null; }
    if (ok) wk.postMessage({ hidden: document.hidden, frozen: frozen });
    var q = wWait; wWait = []; q.forEach(function (cb) { cb(ok); });
  }
  function withWorker(cb) {                     /* cb(true) → transfer to the worker · cb(false) → main thread */
    if (wState) return cb(wState === 1);
    wWait.push(cb);
    if (wk) return;
    try {
      if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined' || !('transferControlToOffscreen' in HTMLCanvasElement.prototype)) throw 0;
      wk = new Worker(URL.createObjectURL(new Blob(['(' + waveWorker.toString() + ')();'], { type: 'text/javascript' })));
      wk.addEventListener('message', function (e) {
        var m = e.data;
        if ('probe' in m) { wSettle(!!m.probe); return; }
        if (m.fail && wCards[m.id]) wCards[m.id]();   /* GL failed on the transferred canvas → main-thread on a fresh one */
      });
      wk.addEventListener('error', function () { wSettle(false); });
      wk.postMessage({ init: { VS: VS, FS: FS, Y0: Y0, Y1: Y1 } });
      setTimeout(function () { wSettle(false); }, 4000);   /* no answer → main thread */
    } catch (e) { wSettle(false); }
  }
  document.addEventListener('visibilitychange', function () { wPost({ hidden: document.hidden }); });

  function build(card) {
    var chapter = card.closest('.ft-saga__chapter') || card.parentNode;
    var P = PAL[(card.getAttribute('data-house') || 'blissence').toLowerCase()] || PAL.blissence;
    var old = card.querySelector('canvas.ft-saga__fluid'); if (old) old.style.display = 'none';   /* v1 square canvas unused */
    var canvas = document.createElement('canvas');
    canvas.className = 'ft-wave-band'; canvas.setAttribute('aria-hidden', 'true');
    chapter.insertBefore(canvas, chapter.firstChild);
    chapter.classList.add('ft-has-wave');
    card.classList.add('ft-live--on');

    var mode = 0;                                 /* 0 not started · 1 main thread · 2 worker */
    var gl = null, U = null, scaleIdx = 0, geo = null, wid = 0;
    var visible = false, running = false, revealAt = 0, revealed = false;
    var t0 = performance.now() - Math.random() * 40000, last = 0, frameMs = FRAME_MS;
    var slow = 0, sampled = 0;

    function reveal() { if (!revealed) { revealed = true; card.classList.add('ft-live--revealed'); } }
    function startClock() {
      if (revealAt) return;
      revealAt = performance.now();
      if (mode === 2) wk.postMessage({ id: wid, revealAt: performance.timeOrigin + revealAt });
    }

    function initGL() {
      if (gl) return true;
      gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, powerPreference: 'low-power', preserveDrawingBuffer: false });
      if (!gl) { card.classList.add('ft-live--revealed'); return false; }
      function sh(type, src) { var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; }
      var prog = gl.createProgram();
      gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link');
      gl.useProgram(prog);
      var buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      var loc = gl.getAttribLocation(prog, 'aPos'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      U = {}; ['uMap', 'uPx', 'uWide', 'uTime', 'uReveal', 'uBody', 'uCrest'].forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });
      gl.uniform3fv(U.uBody, hex(P.body)); gl.uniform3fv(U.uCrest, hex(P.crest));
      gl.clearColor(0, 0, 0, 0);
      gl.disable(gl.DITHER);
      canvas.addEventListener('webglcontextlost', function (e) { e.preventDefault(); gl = null; running = false; }, false);
      layout();
      return true;
    }

    /* desktop: band spans the viewport width; mobile: band spans just the card (+ the fade) */
    function layout() {
      var cr = card.getBoundingClientRect(), hr = chapter.getBoundingClientRect();
      if (!cr.width || !cr.height) return;
      var wide = window.innerWidth >= 990;
      var vw = document.documentElement.clientWidth;
      var top = (cr.top - hr.top) + (1 - Y1) * cr.height;
      var hgt = (Y1 - Y0) * cr.height;
      var left, width;
      if (wide) { left = -hr.left; width = vw; }
      else { width = Math.min(vw, cr.width * 1.3); left = (cr.left - hr.left) + cr.width / 2 - width / 2; }   /* .64 card fade → ±0.65 card */
      canvas.style.top = top + 'px'; canvas.style.height = hgt + 'px';
      canvas.style.left = left + 'px'; canvas.style.width = width + 'px';
      geo = { w: width, hgt: hgt, x0: hr.left + left, cardL: cr.left, cardW: cr.width, wide: wide };
      if (mode === 2) wk.postMessage({ id: wid, dpr: window.devicePixelRatio || 1, geo: geo });   /* backing store is sized in the worker */
      else applyScale();
    }
    function applyScale() {
      if (!gl || !geo) return;
      var s = Math.min(SCALES[scaleIdx], window.devicePixelRatio || 1);
      var W = Math.max(64, Math.round(geo.w * s)), H = Math.max(16, Math.round(geo.hgt * s));
      if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }
      gl.viewport(0, 0, W, H);
      gl.uniform4f(U.uMap, geo.w / geo.cardW, (geo.x0 - geo.cardL) / geo.cardW, Y1 - Y0, Y0);
      gl.uniform1f(U.uPx, (Y1 - Y0) / H);
      gl.uniform1f(U.uWide, geo.wide ? 1 : 0);
    }

    /* main-thread renderer — only where the worker path is unavailable (kept exactly as approved) */
    function frame(now) {
      if (!visible || document.hidden || !gl) { running = false; return; }
      var dt = now - last;
      if (!frozen && dt >= frameMs - 1) {
        if (last && revealed) {                     /* adaptive resolution for budget phones */
          sampled++; if (dt > frameMs * 1.6) slow++;
          if (sampled >= 45) { if (slow > 18 && scaleIdx < SCALES.length - 1) { scaleIdx++; applyScale(); } sampled = 0; slow = 0; }
        }
        last = now;
        var rv = revealAt ? Math.min(1, (now - revealAt) / 1100) : 0; rv = rv * rv * (3 - 2 * rv);
        if (!revealed && rv > 0.2) reveal();
        gl.uniform1f(U.uTime, (now - t0) / 1000); gl.uniform1f(U.uReveal, rv);
        gl.clear(gl.COLOR_BUFFER_BIT); gl.drawArrays(gl.TRIANGLES, 0, 3);
      }
      requestAnimationFrame(frame);
    }
    function wake() {
      if (mode === 2) { wk.postMessage({ id: wid, visible: visible }); return; }
      if (mode === 1 && !running && visible && gl && !document.hidden) { running = true; last = 0; requestAnimationFrame(frame); }
    }
    function startMain() {
      mode = 1;
      try { initGL(); } catch (e) { if (window.console) console.warn('ft-psp-wave', e); card.classList.add('ft-live--revealed'); }
      wake();
    }
    function startWorker() {
      mode = 2; wid = ++wId;
      wCards[wid] = function () {               /* worker could not get GL on this canvas: fresh canvas, main thread */
        delete wCards[wid];
        var fresh = document.createElement('canvas');
        fresh.className = canvas.className; fresh.setAttribute('aria-hidden', 'true'); fresh.style.cssText = canvas.style.cssText;
        bandIO.unobserve(canvas); canvas.parentNode.replaceChild(fresh, canvas); canvas = fresh; bandIO.observe(canvas);
        startMain();
      };
      layout();                                   /* CSS box + geometry before the hand-over */
      var off = canvas.transferControlToOffscreen();
      wk.postMessage({ id: wid, canvas: off, P: P, frameMs: frameMs, SCALES: SCALES, dpr: window.devicePixelRatio || 1, geo: geo,
                      revealAt: revealAt ? performance.timeOrigin + revealAt : 0, visible: visible }, [off]);
    }

    var rz = null;
    function onResize() { clearTimeout(rz); rz = setTimeout(layout, 120); }
    if ('ResizeObserver' in window) new ResizeObserver(onResize).observe(chapter); else window.addEventListener('resize', onResize, { passive: true });

    if (reduce) {
      new IntersectionObserver(function (en, ob) {
        if (!en[0].isIntersecting) return; ob.disconnect();
        mode = 1;
        try { if (initGL()) { gl.uniform1f(U.uTime, 3); gl.uniform1f(U.uReveal, 1); gl.clear(gl.COLOR_BUFFER_BIT); gl.drawArrays(gl.TRIANGLES, 0, 3); } } catch (e) {}
        card.classList.add('ft-live--revealed');
      }, { rootMargin: '100% 0px' }).observe(chapter);
      return;
    }
    new IntersectionObserver(function (en, ob) {                     /* lazy GL (worker or main thread) */
      if (!en[0].isIntersecting) return; ob.disconnect();
      withWorker(function (ok) {
        try { if (ok) startWorker(); else startMain(); }
        catch (e) { if (window.console) console.warn('ft-psp-wave', e); card.classList.add('ft-live--revealed'); }
      });
    }, { rootMargin: '100% 0px' }).observe(chapter);
    /* perf 2026-10-18: the reveal clock + logo emergence stay tied to the CARD entering view (as
       approved); drawing is gated on the BAND itself being on screen — the card observer kept an
       off-screen band rendering whenever any of its card showed (~10% of saga scroll positions). */
    new IntersectionObserver(function (en) {
      if (!en[0].isIntersecting) return;
      startClock();
      setTimeout(reveal, LOGO_MS);
    }).observe(card);
    var bandIO = new IntersectionObserver(function (en) {             /* draw only while the band is on screen */
      visible = en[0].isIntersecting;
      if (visible) startClock();
      wake();
    });
    bandIO.observe(canvas);
    document.addEventListener('visibilitychange', wake);
  }

  function arm(root) {
    (root || document).querySelectorAll('[data-ft-live]:not([data-ft-live-on])').forEach(function (c) {
      c.setAttribute('data-ft-live-on', '');
      try { build(c); } catch (e) { if (window.console) console.warn('ft-psp-wave', e); c.classList.add('ft-live--revealed'); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { arm(); }); else arm();
  document.addEventListener('shopify:section:load', function (e) { arm(e.target); });
})();
